import { memo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { FeatureFlagEnum } from '../../../../../featureflag/feature-flag.enum';
import LangEnum from '../../../../../lang/LangEnum';
import { getSelloutSummaryFromQuantity } from '../../../../../services/modules/product-color-sellout/productColorSellout.utils';
import useProductDetailsStore from '../../../../../stores/ProductDetails/productDetails.store';
import { MassActionCombinationItem } from '../../../../../stores/ProductDetails/productDetails.types';
import snackbarStore from '../../../../../stores/Snackbar.store';
import useSystemParametersStore from '../../../../../stores/SystemParameters/useSystemParameters.store';
import { ProductGridTestIds } from '../../../../../utils/testIds/productGrid.testIds';
import { useAuthContext } from '../../../../login/AuthContext';
import { CopiedGrid } from '../../../../orders/orderState.types';
import { useTranslation } from '../../../../translation/TranslationContext';
import { AvailabilityEnum } from '../../../ProductTypes';
import { isPreOrder } from '../../../utils/availability.utils';
import CombinationLine from '../../components/CombinationLine/components/CombinationLine/CombinationLine';
import useCombinationLineMassActionProps from '../../components/CombinationLine/components/CombinationLine/hooks/useCombinationLineMassActionProps';
import useCombinationMassAction from '../../components/CombinationLine/components/CombinationLine/hooks/useCombinationMassAction';
import { COMBINATION_WITH_IMAGE_INFO_SECTION_WIDTH } from '../../components/CombinationLine/consts/combinationLine.consts';
import { CombinationLineTransformer } from '../../components/CombinationLine/transformers/CombinationLineProps.transformer';
import useCombination from '../../hooks/useCombination';
import { useProductLock } from '../../hooks/useProductLock';
import { IProductDetailsCombination } from '../../types/productDetails.types';
import { mountProductDetailsCombinationItem } from '../../utils/mountProductDetailsCombinationItem';

const ProductDetailsCombination = ({
  disableGrid,
  combination,
  refetchUsingOrderIds,
  isFetchingSellout,
  getIsSuggestedProduct,
  suggestedProducts,
  allOrders,
  selectedOrder,
  selloutQuantitySold,
  isOfflineMode = false,
  validationErrors,
}: IProductDetailsCombination) => {
  const { loggedin } = useAuthContext();
  const { translate } = useTranslation();
  const { getLimit, getStockValue } = useProductLock();
  const { handleMessage } = snackbarStore;

  const productDetailsModalState = useProductDetailsStore(
    (state) => state.productDetailsModalState,
  );

  const minValueToShowSelloutTag = useSystemParametersStore((state) =>
    state.getBrandSystemParameterValue<number>(
      FeatureFlagEnum.COMMERCE_MIN_VALUE_TO_SHOW_SELLOUT_TAG,
    ),
  );

  const foundOrder = allOrders?.find((o) => o.id === combination.orderId);
  const foundGroup = suggestedProducts?.find(
    (suggestedProductsGroup) =>
      suggestedProductsGroup?.customerId ===
      (foundOrder?.customer?.id || productDetailsModalState.customerId),
  );
  const isSuggested = getIsSuggestedProduct(combination, foundGroup);

  const selloutData = getSelloutSummaryFromQuantity({
    isFetching: isFetchingSellout,
    quantitySold: selloutQuantitySold,
    minValue: minValueToShowSelloutTag,
  });

  const { copiedGrid, productGridParams, relatedProductsFilterState } = useProductDetailsStore(
    useShallow((state) => ({
      copiedGrid: state.copiedGrid,
      productGridParams: state.productGridParams,
      relatedProductsFilterState: state.relatedProductsFilterState,
    })),
  );

  const { copyGrid, removeCopiedGrid, toggleCombinationMassActionSelection } =
    useProductDetailsStore(
      useShallow((state) => ({
        copyGrid: state.copyGrid,
        removeCopiedGrid: state.removeCopiedGrid,
        toggleCombinationMassActionSelection: state.toggleCombinationMassActionSelection,
      })),
    );

  const selloutSummary =
    productGridParams.isFinishedOrder || selectedOrder?.customer?.id === foundOrder?.customer?.id
      ? selloutData
      : undefined;

  const {
    grid,
    combinationKey,
    hasEmptyAvailability,
    suggestedGrid,
    hasValidPacks,
    selectedPacks,
    partsQuantity,
    suggestedPacks,
    isLoginModalOpen,
    gridButtonStatus,
    productSizeNames,
    showSuggestedGrid,
    shouldDisableGrid,
    suggestionAchieved,
    shouldDisableAddButton,
    combinationOrderStatus,
    shouldDisableGridBasedOnCustomerAvailability,
    stackOrderItem,
    closeLoginModal,
    handleClickPaste,
    onAddOrderProduct,
    handleSelectPacks,
    resetSelectedPacks,
    ensureSelectedOrder,
    onRemoveOrderProduct,
    getAvailabilityLabel,
    selectPacksSideEffect,
    handleSelectSuggestion,
  } = useCombination({
    disableGrid,
    combination,
    isSuggested,
    isOfflineMode,
    refetchUsingOrderIds,
  });

  const orderProductGridContainerProps =
    CombinationLineTransformer.toOrderProductGridContainerProps({
      grid,
      combinationKey,
      copiedGrid,
      combination,
      validationErrors,
      hasValidPacks,
      suggestedGrid,
      selectedPacks,
      suggestedPacks,
      productSizeNames,
      showSuggestedGrid,
      suggestionAchieved,
      selloutSummary,
      hideButtons: shouldDisableGridBasedOnCustomerAvailability,
      disableAll: shouldDisableGrid || selectedPacks.length > 0,
      orderId: combination.orderId,
      orderStatus: combinationOrderStatus,
      readMode: productGridParams.isFinishedOrder,
      getLimit,
      getStockValue,
      stackOrderItem,
      removeCopiedGrid,
      handleSelectPacks,
      resetSelectedPacks,
      handleSelectSuggestion,
      onClickPaste: handleClickPaste,
      selectPacksSideEffect: selectPacksSideEffect,
      copyGrid: (gridToCopy: CopiedGrid) => ensureSelectedOrder(() => copyGrid(gridToCopy)),
    });

  const { isCopiedGridLine, disablePasteAction, isPasteBlockedByPacks, pasteStatus } =
    useCombinationMassAction({
      orderId: orderProductGridContainerProps.orderId,
      product: orderProductGridContainerProps.product,
      grid: orderProductGridContainerProps.grid,
      copiedGrid: orderProductGridContainerProps.copiedGrid,
      disableGrid,
    });

  const deliveryWindowId =
    combination.availability === AvailabilityEnum.FUTURE_STOCK
      ? combination.futureStockDeliveryWindowId
      : (combination.deliveryWindowId ?? undefined);

  const combinationItem = mountProductDetailsCombinationItem({
    productColorId: combination.productColorId,
    availability: combination.availability,
    deliveryWindowId: deliveryWindowId,
    orderId: combination.orderId,
  });

  const toggleSelectedCombinationItem = (item: MassActionCombinationItem) => {
    if (!combination.orderId) {
      return;
    }

    if (isPreOrder(combination.availability) && !deliveryWindowId) {
      handleMessage({
        type: 'warning',
        message: translate(LangEnum.ADD_ORDER_PRODUCTS_EMPTY_AVAILABILITY_WARNING_MESSAGE),
      });
      return;
    }

    toggleCombinationMassActionSelection(item);
  };

  const { massActionProps, pasteStateButtonProps } = useCombinationLineMassActionProps({
    pasteStatus,
    combinationItem,
    isCopiedGridLine,
    disablePasteAction,
    isPasteBlockedByPacks,
    areGridButtonsHidden: shouldDisableGridBasedOnCustomerAvailability,
    onClickPaste: handleClickPaste,
    onToggleSelectedItem: toggleSelectedCombinationItem,
  });

  return (
    <CombinationLine
      isLoggedin={loggedin}
      gridLength={grid.length}
      combinationTestId={`${ProductGridTestIds.GRID_COMBINATION}-${combination.productColorCode}-${combination.availability}`}
      massActionProps={massActionProps}
      pasteStateButtonProps={pasteStateButtonProps}
      closeLoginModal={closeLoginModal}
      isLoginModalOpen={isLoginModalOpen}
      hideCombinationPrice={shouldDisableGridBasedOnCustomerAvailability}
      imageUrl={combination.productColorMediaUrl}
      showProductImage={relatedProductsFilterState}
      orderProductGridContainerProps={orderProductGridContainerProps}
      infoSectionWidth={
        relatedProductsFilterState ? COMBINATION_WITH_IMAGE_INFO_SECTION_WIDTH : undefined
      }
      orderProductPriceProps={{
        grid,
        partsQuantity,
        combinationKey,
        isFinishedOrder: !!productGridParams.isFinishedOrder,
      }}
      productDetailsAddOrderButtonV2Props={{
        gridButtonStatus,
        combinationKey,
        hasEmptyAvailability,
        disabled: shouldDisableAddButton,
        tooltipMessage: shouldDisableGridBasedOnCustomerAvailability
          ? LangEnum.API_EXCEPTION_CUSTOMER_UNAUTHORIZED_PRODUCT
          : undefined,
        onAdd: onAddOrderProduct,
        onRemove: onRemoveOrderProduct,
      }}
      productDetailsCombinationColorAndTagsProps={CombinationLineTransformer.toProductDetailsCombinationColorAndTagsProps(
        {
          combination,
          showSuggestedGrid,
          suggestedGrid,
          availabilityLabel: getAvailabilityLabel(),
          showProductName: relatedProductsFilterState,
          showColorIcon: !relatedProductsFilterState,
        },
      )}
    />
  );
};

export default memo(ProductDetailsCombination);
